import { Link } from 'react-router-dom';
import { t } from '@app/i18n';
import { Badge, Drawer, ErrorBanner } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import { dataHour } from '@app/format';
import { useAuditEntry } from './Audit.api';
import { entityLink, resultView, type AuditEntryDetail } from './AuditModel';
import s from './Audit.module.css';

export interface AuditDetailDrawerProps {
  id: string;
  onClose: () => void;
}

/** Documento resumido do registro — sempre em monoespaçada, nunca editável. */
function Document({ title, dados }: { title: string; dados: unknown }) {
  return (
    <section className={s.bloco}>
      <h3>{title}</h3>
      {dados ? (
        <pre className={s.json}>{JSON.stringify(dados, null, 2)}</pre>
      ) : (
        <p className={s.vazio}>{t('audit.drawer.document.empty')}</p>
      )}
    </section>
  );
}

function ExecutorResponse({ detail }: { detail: AuditEntryDetail }) {
  const respBody = detail.middleware_response;
  if (!respBody) {
    return (
      <section className={s.bloco}>
        <h3>{t('audit.drawer.middleware.title')}</h3>
        <p className={s.vazio}>{t('audit.drawer.middleware.notCalled')}</p>
      </section>
    );
  }

  return (
    <section className={s.bloco}>
      <h3>{t('audit.drawer.middleware.title')}</h3>
      <dl className={s.meta}>
        <dt>{t('audit.drawer.middleware.status')}</dt>
        <dd className={s.mono}>{respBody.status ?? '—'}</dd>
        <dt>{t('audit.drawer.middleware.code')}</dt>
        <dd className={s.mono}>{respBody.code ?? '—'}</dd>
        <dt>{t('audit.drawer.middleware.rollback')}</dt>
        {/* `desfeito: false` é o que separa "nada foi gravado" de "sobrou estado". */}
        <dd>
          {respBody.undone === null || respBody.undone === undefined
            ? '—'
            : t(
                respBody.undone
                  ? 'audit.drawer.middleware.rollback.yes'
                  : 'audit.drawer.middleware.rollback.no',
              )}
        </dd>
      </dl>

      {respBody.steps?.length ? (
        <ul className={s.passos}>
          {respBody.steps.map((step, i) => (
            <li key={`${step.target}-${i}`} className={step.ok ? s.passoOk : s.passoFalha}>
              <Icon name={step.ok ? 'check' : 'close'} size="xs" aria-hidden="true" />
              <span className={s.mono}>{step.target}</span>
              {step.code ? <span className={s.mono}>{step.code}</span> : null}
              {step.detail ? <span className={s.passoDetalhe}>{step.detail}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Detalhe do registro de auditoria (T23, FR-111, FR-113).
 *
 * Mostra antes/depois, o que foi enviado ao executor e a resposta dele (passos e
 * códigos) — nunca segredos: o backend já grava `"***"` no lugar e reaplica a
 * máscara na saída (FR-112). A tela não tem nada a esconder porque não recebe.
 */
export function AuditDetailDrawer({ id, onClose }: AuditDetailDrawerProps) {
  const entry = useAuditEntry(id);
  const detail = entry.data;
  const resultado = detail ? resultView(detail) : null;
  const link =
    detail?.link ?? (detail ? entityLink(detail.entity_kind, detail.entity_id) : null);

  return (
    <Drawer
      open
      title={detail ? detail.action : t('audit.drawer.fallbackTitle')}
      subtitle={detail ? (dataHour(detail.when) ?? undefined) : undefined}
      onClose={onClose}
    >
      {entry.isLoading ? <p className={s.vazio}>{t('audit.drawer.loading')}</p> : null}

      {entry.isError ? (
        <ErrorBanner
          error={entry.error}
          title={t('audit.drawer.error')}
          onRetry={() => void entry.refetch()}
          hideLink
        />
      ) : null}

      {detail ? (
        <>
          <dl className={s.meta}>
            <dt>{t('audit.drawer.field.when')}</dt>
            <dd>{dataHour(detail.when)}</dd>

            <dt>{t('audit.drawer.field.who')}</dt>
            <dd>
              {detail.user}
              {/* `'plataforma'` é o VALOR que o backend grava em `usuario`, não
                  o rótulo da tela: comparar com o texto traduzido quebraria em
                  `en` — a mesma armadilha que o `tsc` pegou 11 vezes em
                  Aprovações, e que aqui ele NÃO pegaria (os dois são `string`). */}
              {detail.user === 'plataforma' ? (
                <span className={s.nota}>{t('audit.platform.automatic')}</span>
              ) : null}
            </dd>

            <dt>{t('audit.drawer.field.entity')}</dt>
            <dd>
              <span className={s.mono}>{detail.entity_kind}</span>
              {detail.entity_name ? ` · ${detail.entity_name}` : ''}
              {link ? (
                <Link className={s.link} to={link}>
                  {t('audit.drawer.field.entity.open')}
                </Link>
              ) : null}
            </dd>

            <dt>{t('audit.drawer.field.result')}</dt>
            <dd>{resultado ? <Badge tone={resultado.tom}>{resultado.text}</Badge> : null}</dd>

            <dt>{t('audit.drawer.field.requestId')}</dt>
            <dd className={s.mono}>{detail.request_id ?? '—'}</dd>
          </dl>

          <Document title={t('audit.drawer.document.before')} dados={detail.summary_before} />
          <Document title={t('audit.drawer.document.after')} dados={detail.summary_after} />
          <Document title={t('audit.drawer.document.sent')} dados={detail.sent_to_middleware} />
          <ExecutorResponse detail={detail} />
        </>
      ) : null}
    </Drawer>
  );
}
